import { Link } from 'react-router-dom'
import { PackageOpen, SearchX, ShieldOff, TriangleAlert } from 'lucide-react'
import Button from './Button'
import { kit, transport, table } from './strings'
import { HTTP_STATUS } from '../domain/domainCodes.js'
import './States.css'

/**
 * The four things a data panel can show instead of data. `LoadingOverlay` stays the
 * full-screen curtain (the legacy `#loading`); these are the in-panel versions.
 */

/**
 * Nothing to show. The default glyph is an OPENED box rather than an inbox: an
 * inbox is a place messages arrive, which says the wrong thing under a table of
 * training programmes — an opened, empty container says "we looked, there is
 * nothing in it", which is what this panel means every time it appears.
 */
export function EmptyState({ icon: Icon = PackageOpen, title, description, action }) {
  return (
    <div className="state state--empty" data-testid="state-empty">
      <span className="state__icon" aria-hidden="true">
        <Icon size={20} strokeWidth={1.75} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {description && (
        <p className="state__description">
          <bdi>{description}</bdi>
        </p>
      )}
      {action && <div className="state__action">{action}</div>}
    </div>
  )
}

/**
 * For panels with no shape to hold — a chart, a detail card. A LIST should use
 * `SkeletonRows` instead: it keeps the table's own shape, so the data arriving
 * is a fill rather than the page jumping from an empty panel to a full one.
 *
 * @param {boolean} [inline]  drop the block padding, for a cell or a button
 */
export function LoadingState({ label = kit.loading, inline = false }) {
  return (
    <div
      className={`state state--loading${inline ? ' state--inline' : ''}`}
      data-testid="state-loading"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="state__spinner" aria-hidden="true" />
      {label ? (
        <p className="state__description">
          <bdi>{label}</bdi>
        </p>
      ) : null}
    </div>
  )
}

/**
 * A 403, told apart from a failure.
 *
 * `ClientApp/CLAUDE.md` asks every screen for four states and this was the missing one:
 * `ApiError.denied` has been set on every 403 since `api.js` was written, the wording has
 * existed in both languages as `transport.denied`, and nothing ever rendered either.
 * A refused screen showed "something went wrong".
 *
 * The distinction is not cosmetic. "Something went wrong" invites a retry; "you do not have
 * permission" invites asking an administrator. This product has fourteen manager roles and a
 * licence gate, so a 403 is an ORDINARY outcome here, not an exceptional one — and sending
 * people to retry a screen their role will never open wastes their time and the
 * administrator's. No `action` slot for that reason: there is nothing for the user to press.
 */
export function DeniedState({ title = transport.denied, description }) {
  /* When the server sends no detail of its own, `api.js` falls back to `transport.denied` —
     the same sentence this uses as its title. Printing it twice reads as a stutter. */
  const detail = description && description !== title ? description : undefined

  return (
    <div className="state state--denied" data-testid="state-denied" role="alert">
      <span className="state__icon state__icon--denied" aria-hidden="true">
        <ShieldOff size={20} strokeWidth={1.75} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {detail && (
        <p className="state__description">
          <bdi>{detail}</bdi>
        </p>
      )}
    </div>
  )
}

/**
 * An address that matches no page. It used to be sent somewhere else without a word —
 * the portal to the courses, the panel to its home — so a mistyped or stale link looked
 * like a link that worked (Taiga #1547). It says so, and offers the way home.
 */
export function NotFoundState({ home = '/' }) {
  return (
    <div className="state state--empty" data-testid="state-not-found" role="status">
      <span className="state__icon" aria-hidden="true">
        <SearchX size={20} strokeWidth={1.75} />
      </span>
      <p className="state__title">
        <bdi>{kit.notFound}</bdi>
      </p>
      <p className="state__description">
        <bdi>{kit.notFoundHint}</bdi>
      </p>
      <div className="state__action">
        <Button as={Link} to={home}>
          {kit.goHome}
        </Button>
      </div>
    </div>
  )
}

/** Do not describe a network/server failure as a nonexistent record. */
export function UnavailableRecordState({ error, home }) {
  if (error && ![HTTP_STATUS.FORBIDDEN, HTTP_STATUS.NOT_FOUND].includes(error.status)) {
    return <ErrorState title={table.loadFailed} />
  }
  return <NotFoundState home={home} />
}

/** A load that failed. The title defaults to saying exactly that — never a loading
    sentence, which reads as "wait" over something that will not arrive. */
export function ErrorState({ title = table.loadFailed, description, action }) {
  return (
    <div className="state state--error" data-testid="state-error" role="alert">
      <span className="state__icon state__icon--error" aria-hidden="true">
        <TriangleAlert size={20} strokeWidth={1.75} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {description && (
        <p className="state__description">
          <bdi>{description}</bdi>
        </p>
      )}
      {action && <div className="state__action">{action}</div>}
    </div>
  )
}
